/* ────────────────────────────────────────────────────────────────────────────
   AIOS CART TRACK  —  cart micro-interaction tracking for The Littl.

   WHY: Google Analytics records people entering and leaving the cart, but almost
   nothing about what they DO inside it. No "viewed cart", no "removed", no
   quantity changes, no free-gift appear/remove, no bundle qualify/break. This
   fills that gap so we can finally see where the cart→checkout leak comes from.

   HOW: it watches the live cart (Shopify Dawn <cart-items> + the AIOS custom
   drawer), works out what changed by comparing the cart before and after each
   update, and reports each action through the existing global helper
   window.aiosGA(name, params) — which writes BOTH:
     • a count to GA4 (property 328365271), and
     • a tag on the Microsoft Clarity session (project xh5g8vyb0s) so you can
       open Clarity and watch a recording of only the people who, say, had the
       free gift vanish.

   SAFETY: every listener is wrapped in try/catch and this file never blocks,
   mutates, or re-renders the cart. It is INVISIBLE to shoppers. Worst case on
   any error it simply stops reporting — the cart keeps working.

   Events fired (all custom, no collision with native ecommerce events):
     cart_qty_up / cart_qty_down     {variant, from, to, title}
     cart_line_removed               {variant, title}
     cart_gift_appeared / cart_gift_removed   {}          (the free serum GWP)
     cart_tier_unlock / cart_tier_lost        {tier}       (ship160/gift180/express249)
     cart_bundle_qualified / cart_bundle_lost {tier}       (99 / 135)
     cart_error                      {msg}                 (a change that failed)
     cart_checkout_click             {}
   ──────────────────────────────────────────────────────────────────────────── */
(function () {
  "use strict";

  var GIFT_VARIANT = 50387995656385;            // Free gift: Flyaway Fix Serum
  var TIERS = [                                  // free-shipping rungs, in cents
    { key: "ship160",    at: 16000 },           // free standard shipping
    { key: "gift180",    at: 18000 },           // free serum gift (AU)
    { key: "express249", at: 24900 }            // free express
  ];
  var SCAN_DEBOUNCE = 260;                       // ms — coalesce burst triggers

  // Fire an event through the proven global helper (GA4 + Clarity). Never throws.
  function ga(name, params) {
    try { if (typeof window.aiosGA === "function") window.aiosGA(name, params || {}); } catch (e) {}
    try {
      // Small in-page log so the tracker can be verified without opening GA.
      window.__aiosCartTrackLog = window.__aiosCartTrackLog || [];
      if (window.__aiosCartTrackLog.length < 200)
        window.__aiosCartTrackLog.push({ n: name, p: params || {} });
    } catch (e) {}
  }

  var prev = null;          // last cart snapshot {items, qual}
  var prevBundle = {};      // {"99":bool, "135":bool}
  var scanning = false, scanQueued = false, scanTimer = null;

  function cartUrl() {
    try {
      if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
        return window.Shopify.routes.root + "cart.js";
    } catch (e) {}
    return "/cart.js";
  }

  function snapshot(cart) {
    var items = {}, qual = 0;
    (cart.items || []).forEach(function (it) {
      var vid = Number(it.variant_id);
      items[vid] = { q: it.quantity, title: it.product_title || it.title || "" };
      // Qualifying total = everything except the free gift line (matches the
      // GWP script's own threshold maths).
      if (vid !== GIFT_VARIANT) qual += (it.final_line_price != null ? it.final_line_price : it.line_price) || 0;
    });
    return { items: items, qual: qual, hasGift: items[GIFT_VARIANT] != null };
  }

  function rungIndex(qual) {
    var idx = -1;
    for (var i = 0; i < TIERS.length; i++) { if (qual >= TIERS[i].at) idx = i; }
    return idx;
  }

  // Bundle qualify/break is read from the cart DOM: qualifying lines show a label
  // reading "Bundle available: get any 3 for $99 / $135" (before 3) OR
  // "3 for $99 / $135 redeemed" (at 3+). We sum the quantities of labelled lines per
  // tier; 3+ = the bundle actually applies at checkout.
  // AIOS-BUNDLE-TRACK-FIX 15/07/26: match on "3 for $" — present in BOTH the available
  // and redeemed copy. The old guard required the word "bundle", which the "…redeemed"
  // wording dropped, so cart_bundle_qualified never fired once the cart actually qualified.
  function scanBundle() {
    try {
      var sums = { "99": 0, "135": 0 };
      var labels = document.querySelectorAll("li.discounts__discount");
      for (var i = 0; i < labels.length; i++) {
        var txt = (labels[i].textContent || "").toLowerCase();
        var tier = txt.indexOf("$99") >= 0 ? "99" : (txt.indexOf("$135") >= 0 ? "135" : null);
        if (!tier || txt.indexOf("3 for $") < 0) continue;
        var row = labels[i].closest(".cart-item, tr.cart-item, li.cart-item");
        var qty = 1;
        if (row) {
          var qi = row.querySelector("input.quantity__input, .quantity__input");
          if (qi && qi.value) qty = parseInt(qi.value, 10) || 1;
        }
        sums[tier] += qty;
      }
      ["99", "135"].forEach(function (t) {
        var now = sums[t] >= 3, was = !!prevBundle[t];
        if (now && !was) ga("cart_bundle_qualified", { tier: t });
        else if (!now && was) ga("cart_bundle_lost", { tier: t });
        prevBundle[t] = now;
      });
    } catch (e) {}
  }

  function diff(a, b) {
    try {
      // Quantity + removals on existing lines. A brand-new line is a native
      // add_to_cart (already tracked by Shopify's pixel) so we skip additions.
      Object.keys(b.items).forEach(function (v) {
        var vid = Number(v);
        if (vid === GIFT_VARIANT) return;
        var nowIt = b.items[vid], wasIt = a.items[vid];
        if (!wasIt) return;                        // new line = add, skip
        if (nowIt.q > wasIt.q) ga("cart_qty_up",   { variant: vid, from: wasIt.q, to: nowIt.q, title: nowIt.title });
        else if (nowIt.q < wasIt.q) ga("cart_qty_down", { variant: vid, from: wasIt.q, to: nowIt.q, title: nowIt.title });
      });
      Object.keys(a.items).forEach(function (v) {
        var vid = Number(v);
        if (vid === GIFT_VARIANT) return;
        if (!b.items[vid]) ga("cart_line_removed", { variant: vid, title: a.items[vid].title });
      });

      // Free gift appear / remove.
      if (b.hasGift && !a.hasGift) ga("cart_gift_appeared", {});
      if (a.hasGift && !b.hasGift) ga("cart_gift_removed", {});

      // Free-shipping / gift rungs crossed.
      var iA = rungIndex(a.qual), iB = rungIndex(b.qual), k;
      if (iB > iA) for (k = iA + 1; k <= iB; k++) ga("cart_tier_unlock", { tier: TIERS[k].key });
      else if (iB < iA) for (k = iA; k > iB; k--) ga("cart_tier_lost", { tier: TIERS[k].key });
    } catch (e) {}
    scanBundle();
  }

  function scan() {
    if (scanning) { scanQueued = true; return; }
    scanning = true;
    fetch(cartUrl(), { headers: { Accept: "application/json" }, credentials: "same-origin" })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart || !cart.items) return;
        var now = snapshot(cart);
        if (prev === null) { prev = now; scanBundle(); return; }   // baseline, no events
        diff(prev, now);
        prev = now;
      })
      .catch(function () {})
      .then(function () {
        scanning = false;
        if (scanQueued) { scanQueued = false; scheduleScan(); }
      });
  }

  function scheduleScan() {
    if (scanTimer) clearTimeout(scanTimer);
    scanTimer = setTimeout(scan, SCAN_DEBOUNCE);
  }

  // ── Wiring ────────────────────────────────────────────────────────────────
  function boot() {
    // Baseline the cart once on load.
    scan();

    // 1) Dawn publishes cartUpdate after every AJAX cart change — the primary
    //    trigger. `subscribe`/`PUB_SUB_EVENTS` are bare globals in Dawn.
    try {
      if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined" && PUB_SUB_EVENTS.cartUpdate) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, function () { scheduleScan(); });
      }
    } catch (e) {}

    // 2) Fallback: watch the cart containers for re-renders. This also catches
    //    the free gift auto-adding/removing itself (no click, no pubsub guarantee).
    try {
      if (window.MutationObserver) {
        var mo = new MutationObserver(function () { scheduleScan(); });
        var hook = function (sel) {
          document.querySelectorAll(sel).forEach(function (n) {
            mo.observe(n, { childList: true, subtree: true });
          });
        };
        hook("cart-drawer, #CartDrawer, cart-items, cart-drawer-items, #main-cart-items, .drawer__inner");
        // error text nodes live inside the cart too; the same observer sees them.
      }
    } catch (e) {}

    // 3) A failed quantity change writes visible text into .cart-item__error-text.
    //    Poll those nodes briefly after any cart interaction and report non-empty.
    var lastErr = "";
    function checkErr() {
      try {
        var nodes = document.querySelectorAll(".cart-item__error-text, [id^='Line-item-error'], [id^='CartDrawer-LineItemError']");
        var msg = "";
        for (var i = 0; i < nodes.length; i++) {
          var t = (nodes[i].textContent || "").trim();
          if (t) { msg = t; break; }
        }
        if (msg && msg !== lastErr) { lastErr = msg; ga("cart_error", { msg: msg.slice(0, 80) }); }
        if (!msg) lastErr = "";
      } catch (e) {}
    }
    document.addEventListener("click", function (e) {
      try {
        if (!e.target || !e.target.closest) return;
        if (e.target.closest(".quantity__button, cart-remove-button, .cart-remove-button")) {
          setTimeout(checkErr, 500); setTimeout(checkErr, 1200);
        }
      } catch (err) {}
    }, true);

    // 4) Checkout button click — the exit from the cart.
    document.addEventListener("click", function (e) {
      try {
        if (!e.target || !e.target.closest) return;
        if (e.target.closest("[name='checkout'], #CartDrawer-Checkout, #checkout, .cart__checkout-button, .cart__checkout")) {
          ga("cart_checkout_click", {});
        }
      } catch (err) {}
    }, true);
  }

  // Test/verify handle (read-only, tiny). Lets a harness drive the real diff
  // logic with synthetic cart states, and lets you force a live scan from the
  // console. Never used by the tracker itself.
  try {
    window.__aiosCartTrack = {
      scan: scan,
      snapshot: snapshot,
      rungIndex: rungIndex,
      diff: diff,
      setPrev: function (s) { prev = s; },
      getPrev: function () { return prev; },
      resetBundle: function () { prevBundle = {}; }
    };
  } catch (e) {}

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
})();
